/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import {
  AvatarGenBulkRecord,
  AvatarRecord,
  Box,
  BoxRecord,
  BulkTransactionRecord,
  BulkTransactionSummary,
  DatapointRecord,
  Dataset,
  DatasetExportType,
  DatasetImportType,
  DatasetOpRecord,
  DatasetRecord,
  DatasetSubscription,
  DatasetSubscriptionRecord,
  FundAccountWorkerRecord,
  GenWorkerCodesInfo,
  InitiateWorkerTransactionBody,
  Project,
  ProjectGroupRecord,
  ProjectGroupStatus,
  ProjectRecord,
  ScriptDef,
  ScriptOpOverview,
  ScriptOpRecord,
  ServerRole,
  ServerUser,
  ServerUserRecord,
  PrebatchingProps,
  Task,
  TaskAssignment,
  TaskAssignmentRecord,
  TaskOpRecord,
  TaskParams,
  TaskRecord,
  TransactionWorkerRecord,
  AssignUserToProjectInput,
  UserProjectAcl,
  UserProjectAclRecord,
  VerifiedUserInfoForPayment,
  WorkerRecord,
  UserAccountInfoExtended,
  WorkerPIIField,
  WorkerTaskLedgerResponse,
  WorkerPaymentLedgerResponse,
  TroubleshootingWorkerView,
  TroubleshootingWorkerLookupSuccess,
  OrgConfigRecord,
  OrgPaymentConfigRecord,
  getFinancialYearKey,
  AdditionalPaymentLedgerResponse,
} from '@karya/core';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { apiUserBaseUrl, shouldUseArchivedApiPrefix } from '@/helpers/archivedApi';

/** Response from GET /project/org_config */
export type ProjectOrgConfig = {
  org: OrgConfigRecord;
  paymentConfig: OrgPaymentConfigRecord;
};

/** Minimal user info for project add-user dropdown (from GET /project_users) */
export type ProjectAssignableUser = { id: string; email: string; full_name: string };

/** ACL record with user details (from GET /user_project_acls) - allows COORDINATOR to view list without server_users */
export type UserProjectAclWithUserDetails = UserProjectAclRecord & {
  name?: string;
  email?: string;
  phone_number?: string;
};

/** Project group detail (from GET /project_groups/:id) — group + member projects. */
export type ProjectGroupDetail = ProjectGroupRecord & {
  projects: Pick<ProjectRecord, 'id' | 'code_name' | 'name' | 'status'>[];
};

/**
 * Owning-project summary attached to each row when `GET /datasets` is
 * called with `?include_group=true`. Mirrors `OwningProjectSummary` in
 * `backend/src/datasets/Handlers.ts`. We don't import the backend type
 * here (the frontend doesn't depend on backend) but must keep the shape
 * in sync — see the PRD §5.3 contract.
 */
export type OwningProjectSummary = Pick<ProjectRecord, 'id' | 'code_name' | 'name'>;

/**
 * Dataset row enriched with an `owning_project` annotation. Always
 * returned by `GET /datasets`, regardless of `?include_group`. For same-
 * project rows the annotation is the request's own project, so the UI
 * can render a uniform chip without branching on shape; for sibling rows
 * it surfaces the cross-project provenance.
 *
 * `owning_project_id` is redundant with `project_id` (it's the same
 * value) but the backend emits both for clarity, and we mirror that here
 * so a future divergence is easy to spot.
 *
 * `owning_project` may be `null` only as a defensive fallback if the
 * backend cannot resolve the project row (rare race); UIs should treat
 * it as "unknown owner" and fall back to the dataset's own metadata.
 */
export type DatasetWithOwner = DatasetRecord & {
  owning_project_id: string;
  owning_project: OwningProjectSummary | null;
};

export type GetAllDatasetsArg = { include_group?: boolean } | void;

/** Inputs for project_group endpoints. */
export type CreateProjectGroupArg = {
  code_name: string;
  name: string;
  description?: string | null;
};

export type UpdateProjectGroupArg = {
  id: string;
  code_name?: string;
  name?: string;
  description?: string | null;
  status?: ProjectGroupStatus;
};

export type AddProjectToGroupArg = { group_id: string; project_id: string };

export type RemoveProjectFromGroupArg = { group_id: string; project_id: string; force?: boolean };

export type RemoveProjectFromGroupResult = { affectedSubscriptionIds: string[] };
export type UpdateServerUserRoleArg = { id: string; role: ServerRole };

export type TroubleshootingWorkerLookupArg = { query: string; projectId: string };
export type TroubleshootingWorkerDetailArg = { workerId: string; projectId: string };

/**
 * Query arg types
 */

// Auth endpoints
export type LoginUserQueryArg = {
  google_id_token: string;
};

export type SignupUserQueryArg = {
  google_id_token: string;
  access_code: string;
  server_user: ServerUser;
};

export type IngestDatapointsQueryArg = {
  dataset_id: string;
  import_type: DatasetImportType;
  tgz_option: string | undefined;
  tgz_required_option: string | undefined;
  [key: string]: File | string | DatasetImportType | undefined;
};

export type IngestDatapointQueryArg = {
  dataset_id: string;
  file: File;
  import_type: DatasetImportType;
};

export type CreateExportQueryArg = {
  dataset_id: string;
  export_type: DatasetExportType;
};

export type CreateTaskQueryArg = {
  data: Task;
  files: { [id: string]: File };
  draft_task_id?: string;
};

export type EditTaskQueryArg = {
  id: string;
  data: Task;
  files: { [id: string]: File };
};

export type UpdateTaskPrebatchingArg = PrebatchingProps & { task_id: string };

export type ProjectPrebatchBuildStatus = {
  lastBuildAt: string | null;
  unallocatedCount: number;
  allocatedCount: number;
};

export type TriggerProjectPrebatchBuildResult = {
  summary: {
    tasksScanned: number;
    tasksBuilt: number;
    batchesCreated: number;
    microtasksBatched: number;
  };
  triggeredAt: string;
  status: ProjectPrebatchBuildStatus;
};

export type TaskPrebatchStatus = {
  resolvedEnabled: boolean;
  resolvedDrain: boolean;
  taskFlag?: boolean;
  taskDrain?: boolean;
  projectFlag?: boolean;
  projectDrain?: boolean;
  // Mirrors `EligibilityResult` from @karya/common (frontend cannot depend on common).
  eligibility:
    | {
        eligible: true;
        settings: { enabled: boolean; drain: boolean; assignmentBatchSize: number };
        taskAssignment: TaskAssignmentRecord;
      }
    | { eligible: false; reason: string };
};

export type WorkerMaskedPIIRow = {
  worker_id: string;
  name: string | null;
  phone_number: string;
  id_details: string | null;
  upi: string | null;
  bank_account: string | null;
};

export type WorkerPIIFieldResponse = {
  worker_id: string;
  field: WorkerPIIField;
  value: string | null;
};

export type WorkerTaskSummary = {
  avatar_id: string;
  access_code: string;
  worker_id: string;
  phone_number: string;
  profile?: {
    data: {
      full_name: string;
      yob: string;
      pincode: string;
      gender?: [string];
    };
    name: string;
    device_info: Record<string, unknown>;
  };
  profile_updated_at?: string;
  assigned: number;
  completed: number;
  verified: number;
  total: number;
  // earned: number; we will implement it later
  accepted: number;
  rejected: number;
  expired: number;
  skipped: number;
  earliest?: string;
  latest?: string;
  status: AvatarStatus;
};

export enum AvatarStatus {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
}

export type AutoLinkAvatarQueryArg = {
  phone_numbers: File;
  task_id: string;
  box_id: string;
  worker_tags: string[];
};

export type AutoLinkAvatarWithWorkerIdQueryArg = {
  worker_ids: File;
  task_id: string;
  box_id: string;
  worker_tags: string[];
};

export type ExecuteScriptMutationArg = {
  id: string;
  desc?: string;
  params: Record<string, string | Blob>;
};

export type OrgConfigListItem = {
  id: string;
  org_code: string;
  org_name: string;
};

export type OrgConfigsForProjectCreate = {
  default_org_code: string;
  orgs: OrgConfigListItem[];
};

const backendServerUrl = import.meta.env.VITE_SERVER_URL || '';

/**
 * Api tagTypes
 */
export const apiTags = [
  'current_user',
  'projects',
  'project_groups',
  'server_users',
  'boxes',
  'datasets',
  'datapoints',
  'dataset_ops',
  'tasks',
  'task_draft',
  'dataset_subscriptions',
  'scripts',
  'script_runs',
  'script_op',
  'task_assignments',
  'user_acls',
  'user_project_acl',
  'avatar_bulks',
  'auto_link_avatars',
  'avatar_worker_task_summary',
  'worker_task_ledger',
  'worker_payment_ledger',
  'additional_payment_ledger',
  'worker_payment_accounts',
  'worker_accounts',
  'worker_payment_transactions',
  'eligible_workers_for_payment',
  'bulk_transaction',
  'bulk_transaction_summaries',
  'scripts',
  'script_op',
  'script_runs',
  'assignable_microtask_count',
  'qr_code_payload',
  'worker_masked_pii',
  'task_prebatch_status',
  'project_prebatch_build_status',
  'org_configs',
  'troubleshooting_worker',
] as const;

type CurrentProjectState = {
  currentProject?: {
    project?: { id?: string; status?: string; migration_status?: string } | null;
  };
};

function currentProjectFromState(state: unknown) {
  return (state as CurrentProjectState).currentProject?.project;
}

function createApiUserBaseQuery(useArchived: boolean) {
  return fetchBaseQuery({
    baseUrl: apiUserBaseUrl(backendServerUrl, useArchived),
    credentials: 'include',
    prepareHeaders: (header, api) => {
      const project = currentProjectFromState(api.getState());
      header.set('project_id', project?.id || '');
    },
  });
}

const hotApiUserBaseQuery = createApiUserBaseQuery(false);
const archivedApiUserBaseQuery = createApiUserBaseQuery(true);

/** Routes ARCHIVED + MIGRATED project-scoped calls to `{origin}/archived/api_user`. */
export const apiUserBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = (
  args,
  api,
  extraOptions,
) => {
  const project = currentProjectFromState(api.getState());
  const path = typeof args === 'string' ? args : args.url;
  const query = shouldUseArchivedApiPrefix(project, path) ? archivedApiUserBaseQuery : hotApiUserBaseQuery;
  return query(args, api, extraOptions);
};

/**
 * API queries
 */
export const backendApi = createApi({
  reducerPath: 'backendApi',

  // Base query parameters
  baseQuery: apiUserBaseQuery,

  refetchOnReconnect: true,

  // Tag tyeps
  tagTypes: apiTags,
  // End points
  endpoints: (builder) => ({
    /**
     * Auth endpoints
     */
    getServerUser: builder.query<ServerUserRecord, void>({
      providesTags: ['current_user'],
      query: () => '/server_user',
    }),

    loginUser: builder.mutation<ServerUserRecord, LoginUserQueryArg>({
      query: ({ google_id_token }) => ({
        url: '/server_user/login',
        headers: {
          'reg-mechanism': 'google-id-token',
          'google-id-token': google_id_token,
        },
        method: 'GET',
      }),
      invalidatesTags: ['current_user'],
    }),

    logoutUser: builder.mutation<object, void>({
      query: () => ({ url: '/server_user/logout', method: 'GET' }),
      invalidatesTags: ['current_user'],
    }),

    signupUser: builder.mutation<ServerUserRecord, SignupUserQueryArg>({
      query: ({ google_id_token, access_code, server_user }) => ({
        url: '/server_user/register',
        method: 'PUT',
        headers: {
          'reg-mechanism': 'google-id-token',
          'google-id-token': google_id_token,
          'access-code': access_code,
        },
        body: server_user,
      }),
      invalidatesTags: ['current_user'],
    }),

    /**
     * Project endpoints
     */
    getProjects: builder.query<Array<ProjectRecord>, void>({
      query: () => '/projects',
      providesTags: ['projects'],
    }),

    createProject: builder.mutation<ProjectRecord, Project & { org_code?: string }>({
      query: (project) => ({
        url: '/projects',
        method: 'POST',
        body: project,
      }),
      invalidatesTags: ['projects'],
    }),

    getOrgConfigs: builder.query<OrgConfigsForProjectCreate, void>({
      query: () => '/org_configs',
      providesTags: ['org_configs'],
    }),

    getProjectOrgConfig: builder.query<
      ProjectOrgConfig,
      // projectId is always passed but its value can be undefined (no project selected);
      // it is only used for cache keying — the request uses the project_id header.
      { projectId: string | undefined }
    >({
      query: () => '/project/org_config',
      // Cache per project so switching orgs/projects does not reuse payment config.
      serializeQueryArgs: ({ queryArgs, endpointName }) => `${endpointName}:${queryArgs.projectId ?? ''}`,
    }),

    /**
     * Toggle project-level prebatching flags. The project is resolved from the
     * `project_id` request header (set by `apiUserBaseQuery`), so the body
     * carries only the new flag values.
     */
    updateProjectPrebatching: builder.mutation<ProjectRecord, PrebatchingProps>({
      query: (body) => ({
        url: '/project/prebatching',
        method: 'PUT',
        body,
      }),
      // A project toggle can flip every task's resolved status, so invalidate
      // both the projects list and every cached task prebatch status.
      invalidatesTags: ['projects', 'task_prebatch_status'],
    }),

    getProjectPrebatchBuildStatus: builder.query<ProjectPrebatchBuildStatus, string>({
      query: (projectId) => `/project/${projectId}/prebatch_build_status`,
      providesTags: (_result, _error, projectId) => [{ type: 'project_prebatch_build_status', id: projectId }],
    }),

    triggerProjectPrebatchBuild: builder.mutation<TriggerProjectPrebatchBuildResult, string>({
      query: (projectId) => ({
        url: `/project/${projectId}/prebatch_build`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, projectId) => [{ type: 'project_prebatch_build_status', id: projectId }],
    }),

    /** ACTIVE or INACTIVE → COMPLETED. Project is resolved from the `project_id` header. */
    markProjectCompleted: builder.mutation<ProjectRecord, void>({
      query: () => ({
        url: '/project/complete',
        method: 'POST',
      }),
      invalidatesTags: ['projects'],
    }),

    /** ACTIVE → INACTIVE. Project is resolved from the `project_id` header. */
    markProjectInactive: builder.mutation<ProjectRecord, void>({
      query: () => ({
        url: '/project/inactive',
        method: 'POST',
      }),
      invalidatesTags: ['projects'],
    }),

    /** INACTIVE or COMPLETED → ACTIVE. Project is resolved from the `project_id` header. */
    markProjectActive: builder.mutation<ProjectRecord, void>({
      query: () => ({
        url: '/project/active',
        method: 'POST',
      }),
      invalidatesTags: ['projects'],
    }),

    /** COMPLETED → ARCHIVED. SUPER_ADMIN only. */
    archiveProject: builder.mutation<ProjectRecord, string>({
      query: (projectId) => ({
        url: `/project/${projectId}/archive`,
        method: 'POST',
      }),
      invalidatesTags: ['projects'],
    }),

    /**
     * Project group endpoints (SUPER_ADMIN only on the backend; we surface the
     * UI behind the same role gate). All endpoints share the `project_groups`
     * tag so any mutation refreshes the list and the open detail view, and
     * `projects` is invalidated whenever membership might have shifted so the
     * project list picks up the new project_group_id.
     */
    /**
     * `includeInactive: true` asks the backend to also return groups whose
     * status is INACTIVE. The admin list screen opts in so a deactivated
     * group remains reachable (and reactivatable) from the sidebar; other
     * callers that just want a picker of usable groups can omit it.
     */
    getProjectGroups: builder.query<ProjectGroupRecord[], { includeInactive?: boolean } | void>({
      query: (arg) => (arg && arg.includeInactive ? '/project_groups?include_inactive=true' : '/project_groups'),
      providesTags: ['project_groups'],
    }),

    getProjectGroup: builder.query<ProjectGroupDetail, string>({
      query: (id) => `/project_groups/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'project_groups', id }],
    }),

    createProjectGroup: builder.mutation<ProjectGroupRecord, CreateProjectGroupArg>({
      query: (body) => ({ url: '/project_groups', method: 'POST', body }),
      invalidatesTags: ['project_groups'],
    }),

    updateProjectGroup: builder.mutation<ProjectGroupRecord, UpdateProjectGroupArg>({
      query: ({ id, ...patch }) => ({ url: `/project_groups/${id}`, method: 'PUT', body: patch }),
      invalidatesTags: (_result, _error, arg) => ['project_groups', { type: 'project_groups', id: arg.id }],
    }),

    deleteProjectGroup: builder.mutation<void, string>({
      query: (id) => ({ url: `/project_groups/${id}`, method: 'DELETE' }),
      invalidatesTags: ['project_groups'],
    }),

    addProjectToGroup: builder.mutation<ProjectRecord, AddProjectToGroupArg>({
      query: ({ group_id, project_id }) => ({
        url: `/project_groups/${group_id}/projects`,
        method: 'POST',
        body: { project_id },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'project_groups', id: arg.group_id }, 'projects'],
    }),

    /**
     * Returns the affected subscription ids when force=false. Pass
     * `force: true` to unilaterally deactivate them. Backend responds 409
     * when force is omitted and there are affected subscriptions; callers
     * should handle that by re-prompting the user with the list.
     */
    removeProjectFromGroup: builder.mutation<RemoveProjectFromGroupResult, RemoveProjectFromGroupArg>({
      query: ({ group_id, project_id, force }) => ({
        url: `/project_groups/${group_id}/projects/${project_id}${force ? '?force=true' : ''}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'project_groups', id: arg.group_id },
        'projects',
        // Removing with force may have flipped subscriptions to INACTIVE;
        // refresh the cache so any open subscription views reflect that.
        'dataset_subscriptions',
      ],
    }),

    /**
     * ACL endpoints
     */
    getUserProjectAcls: builder.query<Array<UserProjectAclWithUserDetails>, void>({
      query: () => '/user_project_acls',
      providesTags: ['user_acls'],
    }),

    createUserProjectAcls: builder.mutation<UserProjectAclRecord, AssignUserToProjectInput>({
      query: (user_acl) => ({
        url: '/user_project_acls',
        method: 'POST',
        body: user_acl,
      }),
      invalidatesTags: ['user_acls'],
    }),

    getUserProjectAcl: builder.query<UserProjectAcl, void>({
      query: () => '/user_project_acl',
      providesTags: ['user_project_acl'],
    }),

    /** Users who can be added to the project (excludes INACTIVE, SUPER_ADMIN, already assigned). projectId in cache key. */
    getProjectUsers: builder.query<ProjectAssignableUser[], string | undefined>({
      query: () => '/project_users',
      providesTags: ['user_acls'],
    }),

    /**
     * Server user endpoints
     */
    getAllServerUsers: builder.query<Array<ServerUserRecord>, void>({
      query: () => '/server_users',
      providesTags: ['server_users'],
    }),

    createServerUser: builder.mutation<ServerUserRecord, ServerUser>({
      query: (server_user) => ({
        url: '/server_users',
        method: 'POST',
        body: server_user,
      }),
      invalidatesTags: ['server_users'],
    }),

    updateServerUserRole: builder.mutation<ServerUserRecord, UpdateServerUserRoleArg>({
      query: ({ id, role }) => ({
        url: `/server_users/${id}/role`,
        method: 'PUT',
        body: { role },
      }),
    }),

    /**
     * Avatar endpoints
     */
    generateAvatarCodes: builder.mutation<AvatarRecord[], GenWorkerCodesInfo>({
      query: (avatar_codes_info) => ({
        url: '/avatars',
        method: 'POST',
        body: avatar_codes_info,
      }),
      invalidatesTags: ['avatar_bulks'],
    }),

    getAvatarBulks: builder.query<AvatarGenBulkRecord[], void>({
      query: () => '/avatar_bulks',
      providesTags: ['avatar_bulks'],
    }),

    getAllAvatarsForBulk: builder.mutation<(AvatarRecord & Partial<WorkerRecord>)[], string>({
      query: (bulk_id) => ({
        url: `/avatar_bulks/${bulk_id}/avatars`,
        method: 'GET',
      }),
    }),

    /** Enable/Disable Avatars */
    setAvatarStatus: builder.mutation<
      AvatarRecord,
      { avatar_ids: string[]; status: AvatarStatus; disablePayments: boolean }
    >({
      query: ({ avatar_ids, status, disablePayments }) => ({
        url: '/avatar/status',
        method: 'PUT',
        body: { avatar_ids, status, disablePayments },
      }),
      invalidatesTags: ['avatar_worker_task_summary'],
    }),

    /**
     * Send request for auto linking avatars with phone numbers
     */
    autoLinkAvatarsWithPhone: builder.mutation<TaskOpRecord, AutoLinkAvatarQueryArg>({
      query: ({ task_id, box_id, phone_numbers, worker_tags }) => {
        const body = new FormData();
        body.append('box_id', box_id);
        body.append('phone_numbers', phone_numbers);
        body.append('worker_tags', JSON.stringify(worker_tags));
        return {
          url: `/task/${task_id}/auto_link_avatars`,
          method: 'POST',
          body: body,
          formData: true,
        };
      },
      invalidatesTags: (_result, _error, { task_id }) => [{ type: 'auto_link_avatars', id: task_id }],
    }),

    /**
     * Get auto link avatars job data
     */
    getAutoLinkAvatars: builder.query<TaskOpRecord[], string>({
      query: (task_id) => `/task/${task_id}/auto_link_avatars`,
      providesTags: (_result, _error, task_id) => [{ type: 'auto_link_avatars', id: task_id }],
    }),

    /**
     * Send request for auto linking avatars with worker IDs
     */
    autoLinkAvatarsWithWorkerId: builder.mutation<TaskOpRecord, AutoLinkAvatarWithWorkerIdQueryArg>({
      query: ({ task_id, box_id, worker_ids, worker_tags }) => {
        const body = new FormData();
        body.append('box_id', box_id);
        body.append('worker_ids', worker_ids);
        body.append('worker_tags', JSON.stringify(worker_tags));
        return {
          url: `/task/${task_id}/auto_link_avatars_with_worker_id`,
          method: 'POST',
          body: body,
          formData: true,
        };
      },
      invalidatesTags: (_result, _error, { task_id }) => [{ type: 'auto_link_avatars', id: task_id }],
    }),

    /**
     * Get auto link avatars job data (worker ID linking)
     */
    getAutoLinkAvatarsWithWorkerId: builder.query<TaskOpRecord[], string>({
      query: (task_id) => `/task/${task_id}/auto_link_avatars_with_worker_id`,
      providesTags: (_result, _error, task_id) => [{ type: 'auto_link_avatars', id: task_id }],
    }),

    /**
     * Box endpoints
     */
    getAllBoxes: builder.query<Array<BoxRecord>, void>({
      query: () => '/boxes',
      providesTags: ['boxes'],
    }),

    createBox: builder.mutation<BoxRecord, Box>({
      query: (box) => ({
        url: '/boxes',
        method: 'POST',
        body: box,
      }),
      invalidatesTags: ['boxes'],
    }),

    /**
     * File endpoints
     */
    getShareableUrl: builder.query<{ url: string }, string>({
      query: (filename: string | undefined) => {
        if (filename) {
          return `/utils/files/${filename}`; // TODO: change this endpoint to fileserver, talk to fileserver to get SAS URL, there is a method in common module i.e. getDownloadBlobSasUrl, may be we can use that here.
        } else {
          return `/file`;
        }
      },
    }),

    /**
     * Dataset endpoints
     *
     * Always returns `DatasetWithOwner[]` — every row carries the
     * `owning_project` annotation regardless of `include_group` (PRD
     * §5.3 / §5.4). Same-project rows are annotated with the request's
     * own project so the UI never has to branch on shape; passing
     * `include_group: true` widens the *set* of rows to include sibling
     * projects in the same group.
     */
    getAllDatasets: builder.query<Array<DatasetWithOwner>, GetAllDatasetsArg>({
      query: (arg) => (arg && arg.include_group ? '/datasets?include_group=true' : '/datasets'),
      providesTags: ['datasets'],
    }),

    createDataset: builder.mutation<DatasetRecord, Dataset>({
      query: (dataset) => ({
        url: '/datasets',
        method: 'POST',
        body: dataset,
      }),
      invalidatesTags: ['datasets'],
    }),

    /**
     * Dataset op endpoints
     */
    ingestDatapoints: builder.mutation<DatasetOpRecord, IngestDatapointsQueryArg>({
      query: ({ dataset_id, import_type, ...files }) => {
        const body = new FormData();
        body.append('import_type', import_type);
        for (const [type, file] of Object.entries(files)) {
          body.append(type, file!);
        }
        return {
          url: `/dataset/${dataset_id}/ingest`,
          method: 'POST',
          body,
          formData: true,
        };
      },
      invalidatesTags: ['dataset_ops', 'datasets', 'datapoints'],
    }),

    ingestDatapointFromFile: builder.mutation<DatapointRecord, IngestDatapointQueryArg>({
      query: ({ dataset_id, file, import_type }) => {
        const body = new FormData();
        body.append('import_type', import_type);
        body.append('file', file!);
        return {
          url: `/dataset/${dataset_id}/ingest_file`,
          method: 'POST',
          body,
          formData: true,
        };
      },
      invalidatesTags: ['dataset_ops', 'datasets', 'datapoints'],
    }),

    /**
     * Get datapoints
     */
    getDatapoints: builder.query<Array<DatapointRecord>, string>({
      query: (dataset_id) => `/dataset/${dataset_id}/datapoints`,
      providesTags: ['datapoints'],
    }),

    /**
     * Get dataset ops
     */
    getDatasetOps: builder.query<Array<DatasetOpRecord>, string>({
      query: (dataset_id) => `/dataset/${dataset_id}/ops`,
      providesTags: ['dataset_ops'],
    }),

    /**
     * Create an export operation
     */
    createDatasetExport: builder.mutation<DatasetOpRecord, CreateExportQueryArg>({
      query: ({ dataset_id, export_type }) => {
        return {
          url: `/dataset/${dataset_id}/export`,
          method: 'POST',
          body: { export_type },
        };
      },
      invalidatesTags: ['dataset_ops'],
    }),

    /**
     * Task endpoints
     */
    getAllTasks: builder.query<Array<TaskRecord>, void>({
      query: () => '/tasks',
      providesTags: ['tasks'],
    }),

    getTaskCreationDraft: builder.query<TaskRecord | null, void>({
      query: () => '/tasks/draft',
      transformResponse: (body: { draft: TaskRecord | null }) => body.draft,
      providesTags: ['task_draft'],
    }),

    saveTaskCreationDraft: builder.mutation<TaskRecord, { params: TaskParams; files: { [id: string]: File } }>({
      query: ({ params, files }) => {
        const body = new FormData();
        body.append('data', JSON.stringify({ params }));
        Object.entries(files).forEach(([key, file]) => {
          body.append(key, file);
        });
        return {
          url: '/tasks/draft',
          method: 'PUT',
          body,
          formData: true,
        };
      },
      invalidatesTags: ['task_draft'],
    }),

    getTaskById: builder.query<TaskRecord, string>({
      query: (task_id) => `/task/${task_id}`,
      providesTags: (_result, _error, arg) => [{ type: 'tasks', id: arg }],
    }),

    getAvatarWorkerReport: builder.query<WorkerTaskSummary[], string>({
      query: (id: string) => `/task/${id}/avatar_worker_task_summary`,
      providesTags: (_result, _error, arg) => [{ type: 'avatar_worker_task_summary', id: arg }],
    }),

    getAssignableMicrotaskCount: builder.query<
      {
        assignable_microtask_count: number;
        assignable_test_microtask_count: number;
      },
      string
    >({
      query: (task_id: string) => `/task/${task_id}/assignable_microtask_count`,
      providesTags: (_result, _error, arg) => [{ type: 'assignable_microtask_count', id: arg }],
    }),

    getQRCodePayload: builder.query<{ taskId: string; token: string }, string>({
      query: (task_id: string) => `/task/${task_id}/qr_code_payload`,
      providesTags: (_result, _error, arg) => [{ type: 'qr_code_payload', id: arg }],
    }),

    getTaskPrebatchStatus: builder.query<TaskPrebatchStatus, string>({
      query: (task_id) => `/task/${task_id}/prebatch_status`,
      providesTags: (_result, _error, arg) => [{ type: 'task_prebatch_status', id: arg }],
    }),

    updateTaskPrebatching: builder.mutation<TaskRecord, UpdateTaskPrebatchingArg>({
      query: ({ task_id, ...body }) => ({
        url: `/task/${task_id}/prebatching`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        'tasks',
        { type: 'tasks', id: arg.task_id },
        { type: 'task_prebatch_status', id: arg.task_id },
      ],
    }),

    /**
     * Get scripts executable from current project
     */
    getAllScripts: builder.query<ScriptDef[], void>({
      query: () => '/scripts',
      providesTags: ['scripts'],
    }),

    /**
     * Execute a script
     */
    executeScript: builder.mutation<ScriptOpRecord, ExecuteScriptMutationArg>({
      query: ({ id, desc, params }) => {
        const body = new FormData();
        Object.entries(params).forEach(([key, value]) => {
          body.append(key, value);
        });
        let url = `/scripts/exec/${id}`;

        if (desc) {
          url += `?desc=${desc}`;
        }

        return {
          url,
          method: 'POST',
          body: body,
          formData: true,
        };
      },
    }),

    /**
     * Get status of all the script runs
     */
    getAllScriptRuns: builder.query<ScriptOpOverview[], string>({
      query: (scriptId) => `/scripts/runs/${scriptId}`,
      providesTags: ['script_runs'],
    }),

    /**
     * Get datails of a specific script_op
     */
    getScriptRunDetails: builder.query<ScriptOpRecord, string>({
      query: (opId) => `/scripts/op/${opId}`,
      providesTags: (_result, _error, arg) => [{ type: 'script_op', id: arg }],
    }),

    createTask: builder.mutation<TaskRecord, CreateTaskQueryArg>({
      query: ({ data, files, draft_task_id }) => {
        const body = new FormData();
        const payload = draft_task_id ? { ...data, draft_task_id } : data;
        body.append('data', JSON.stringify(payload));
        Object.entries(files).forEach(([key, file]) => {
          body.append(key, file);
        });
        return {
          url: '/tasks',
          method: 'POST',
          body,
          formData: true,
        };
      },
      // TODO: Task should ideally not invalidate all datasets. Can be optimized
      invalidatesTags: ['tasks', 'datasets', 'task_draft'],
    }),

    editTask: builder.mutation<TaskRecord, EditTaskQueryArg>({
      query: ({ data, files, id }) => {
        const body = new FormData();
        body.append('data', JSON.stringify(data));
        Object.entries(files).forEach(([key, file]) => {
          body.append(key, file);
        });
        return {
          url: `/task/${id}`,
          method: 'PUT',
          body,
          formData: true,
        };
      },
      // TODO: Task should ideally not invalidate all datasets. Can be optimized
      invalidatesTags: ['tasks', 'datasets'],
    }),

    markTaskComplete: builder.mutation<TaskRecord, string>({
      query: (id) => ({
        url: `/task/${id}/mark_complete`,
        method: 'PUT',
      }),
      invalidatesTags: ['tasks'],
    }),

    moveTaskToSubmitted: builder.mutation<TaskRecord[], string>({
      query: (id) => ({
        url: `/task/${id}/move_to_submitted`,
        method: 'POST',
      }),
      // Purge + promotion changes many task-scoped endpoints; invalidate tags the detail screen caches.
      invalidatesTags: (result, _err, rootTaskId) => {
        const taskIds = result?.length ? result.map((t) => t.id) : [rootTaskId];
        const perTaskTags = taskIds.flatMap((id) => [
          { type: 'tasks' as const, id },
          { type: 'assignable_microtask_count' as const, id },
          { type: 'qr_code_payload' as const, id },
          { type: 'avatar_worker_task_summary' as const, id },
          { type: 'auto_link_avatars' as const, id },
        ]);
        return [
          'tasks',
          'datasets',
          'dataset_subscriptions',
          'task_assignments',
          'datapoints',
          'avatar_bulks',
          ...perTaskTags,
        ];
      },
    }),

    /**
     * Dataset subscription endpoints
     */
    getDatasetSubscriptions: builder.query<Array<DatasetSubscriptionRecord>, string | undefined>({
      query: (task_id) => (task_id ? `/dataset_subscriptions?task_id=${task_id}` : `/dataset_subscriptions`),
      providesTags: ['dataset_subscriptions'],
    }),

    createSubscription: builder.mutation<DatasetSubscriptionRecord, DatasetSubscription>({
      query: (dataset_subscription) => ({
        url: '/dataset_subscriptions',
        method: 'POST',
        body: dataset_subscription,
      }),
      invalidatesTags: ['dataset_subscriptions'],
    }),

    /**
     * Task assignment endpoints
     */
    getTaskAssignments: builder.query<Array<TaskAssignmentRecord>, string | undefined>({
      query: (task_id) => (task_id ? `/task_assignments?task_id=${task_id}` : `/task_assignments`),
      providesTags: ['task_assignments'],
    }),

    createTaskAssignment: builder.mutation<TaskAssignmentRecord, TaskAssignment>({
      query: (task_assignment) => ({
        url: '/task_assignments',
        method: 'POST',
        body: task_assignment,
      }),
      invalidatesTags: ['task_assignments'],
    }),

    editTaskAssignment: builder.mutation<TaskAssignmentRecord, TaskAssignment>({
      query: (task_assignment) => ({
        url: `/task_assignment/${task_assignment.id}`,
        method: 'PUT',
        body: task_assignment,
      }),
      invalidatesTags: ['task_assignments'],
    }),

    lookupTroubleshootingWorker: builder.query<TroubleshootingWorkerLookupSuccess, TroubleshootingWorkerLookupArg>({
      query: ({ query }) => ({
        url: '/troubleshooting/worker/lookup',
        params: { query },
      }),
      serializeQueryArgs: ({ queryArgs, endpointName }) => `${endpointName}:${queryArgs.projectId}:${queryArgs.query}`,
      providesTags: (_result, _error, { projectId, query }) => [
        { type: 'troubleshooting_worker', id: `${projectId}:${query}` },
      ],
    }),

    getTroubleshootingWorker: builder.query<TroubleshootingWorkerView, TroubleshootingWorkerDetailArg>({
      query: ({ workerId }) => `/troubleshooting/worker/${workerId}`,
      serializeQueryArgs: ({ queryArgs, endpointName }) =>
        `${endpointName}:${queryArgs.projectId}:${queryArgs.workerId}`,
      providesTags: (_result, _error, { projectId, workerId }) => [
        { type: 'troubleshooting_worker', id: `${projectId}:${workerId}` },
      ],
    }),

    /**
     * Get worker masked PII data for a project
     */
    getWorkerMaskedPII: builder.query<WorkerMaskedPIIRow[], void>({
      query: () => '/worker/masked_pii',
      providesTags: ['worker_masked_pii'],
    }),

    /** Reveal unmasked worker PII field value */
    revealWorkerPIIField: builder.mutation<
      WorkerPIIFieldResponse,
      { workerId: string; field: WorkerPIIField; reason: string }
    >({
      query: ({ workerId, field, reason }) => ({
        url: `/worker/${workerId}/pii_field`,
        method: 'POST',
        body: { field, reason },
      }),
    }),

    /**
     * Payments endpoint
     */
    getWorkerTaskLedger: builder.query<
      WorkerTaskLedgerResponse,
      // projectId is always passed but its value can be undefined (no project selected);
      { refreshCount: number; financialYearKey: string; projectId: string | undefined; forceRefresh: boolean }
    >({
      query: ({ forceRefresh, financialYearKey }) => ({
        url: '/payments/worker_task_ledger',
        params: {
          forceRefresh: forceRefresh ? 'true' : 'false',
          financialYearKey,
        },
      }),
      // Cache per (project, FY).
      serializeQueryArgs: ({ queryArgs, endpointName }) =>
        `${endpointName}:${queryArgs.projectId ?? ''}:${queryArgs.financialYearKey}`,
      // refreshCount is only a nonce to trigger the refetch when Refresh is clicked.
      forceRefetch: ({ currentArg, previousArg }) =>
        (currentArg?.refreshCount ?? 0) > 0 && currentArg?.refreshCount !== previousArg?.refreshCount,
      providesTags: ['worker_task_ledger'],
    }),

    getWorkerPaymentLedger: builder.query<
      WorkerPaymentLedgerResponse,
      // projectId is always passed but its value can be undefined (no project selected);
      { refreshCount: number; financialYearKey: string; projectId: string | undefined; forceRefresh: boolean }
    >({
      query: ({ forceRefresh, financialYearKey }) => ({
        url: '/payments/worker_payment_ledger',
        params: {
          forceRefresh: forceRefresh ? 'true' : 'false',
          financialYearKey,
        },
      }),
      // Cache per (project, FY).
      serializeQueryArgs: ({ queryArgs, endpointName }) =>
        `${endpointName}:${queryArgs.projectId ?? ''}:${queryArgs.financialYearKey}`,
      // refreshCount is only a nonce to trigger the refetch when Refresh is clicked.
      forceRefetch: ({ currentArg, previousArg }) =>
        (currentArg?.refreshCount ?? 0) > 0 && currentArg?.refreshCount !== previousArg?.refreshCount,
      providesTags: ['worker_payment_ledger'],
    }),

    getAdditionalPaymentLedger: builder.query<
      AdditionalPaymentLedgerResponse,
      { refreshCount: number; financialYearKey: string; projectId: string | undefined; forceRefresh: boolean }
    >({
      query: ({ forceRefresh, financialYearKey }) => ({
        url: '/payments/additional_payment_ledger',
        params: {
          forceRefresh: forceRefresh ? 'true' : 'false',
          financialYearKey,
        },
      }),
      serializeQueryArgs: ({ queryArgs, endpointName }) =>
        `${endpointName}:${queryArgs.projectId ?? ''}:${queryArgs.financialYearKey}`,
      forceRefetch: ({ currentArg, previousArg }) =>
        (currentArg?.refreshCount ?? 0) > 0 && currentArg?.refreshCount !== previousArg?.refreshCount,
      providesTags: ['additional_payment_ledger'],
    }),

    getWorkerPaymentAccounts: builder.query<FundAccountWorkerRecord[], void>({
      query: () => '/payments/accounts',
      providesTags: ['worker_payment_accounts'],
    }),

    getWorkerPaymentTransactions: builder.query<TransactionWorkerRecord[], void>({
      query: () => '/payments/transactions',
      providesTags: ['worker_payment_transactions'],
    }),

    getAllEligibleWorkersForPayment: builder.query<VerifiedUserInfoForPayment[], { financialYearKey: string } | void>({
      query: (arg) => {
        const financialYearKey =
          arg && typeof arg === 'object' && arg.financialYearKey
            ? arg.financialYearKey
            : getFinancialYearKey(new Date());
        return { url: '/payments/workers', params: { financialYearKey } };
      },
      providesTags: ['eligible_workers_for_payment'],
    }),

    getAllWorkerAccountsInfo: builder.query<UserAccountInfoExtended[], void>({
      query: () => '/payments/worker_accounts',
      providesTags: ['worker_accounts'],
    }),

    getAllBulkTransactions: builder.query<BulkTransactionRecord[], void>({
      query: () => '/payments/bulk_transactions',
      providesTags: ['bulk_transaction'],
    }),

    getBulkTransactionSummaries: builder.query<BulkTransactionSummary[], void>({
      query: () => '/payments/bulk_transaction_summaries',
      providesTags: ['bulk_transaction_summaries'],
    }),

    createBulkTransaction: builder.mutation<
      TaskOpRecord,
      { bulkTransactionRequestBody: InitiateWorkerTransactionBody[] }
    >({
      query: ({ bulkTransactionRequestBody }) => ({
        url: '/payments/bulk_transaction',
        method: 'POST',
        body: { bulk_transaction_request_body: bulkTransactionRequestBody },
      }),
      invalidatesTags: [
        'bulk_transaction',
        'bulk_transaction_summaries',
        'eligible_workers_for_payment',
        'worker_payment_ledger',
      ],
    }),
  }),
});

export type ApiResult<ResponseType> = {
  isLoading: boolean;
  isSuccess: boolean;
  error?: unknown;
  data?: ResponseType;
};
export type ApiMutationHook<ResponseType, RequestType> = () => readonly [
  (arg: RequestType) => { unwrap(): Promise<ResponseType> },
  ApiResult<ResponseType>,
];

export type ApiQueryHook<RequestType, ResponseType> = (arg: RequestType) => {
  data?: ResponseType;
  currentData?: ResponseType;
  isFetching: boolean;
  isSuccess: boolean;
};
